import { logError } from '@databases/logs';
// FIXME
// eslint-disable-next-line workspaces/require-dependency
import { type DefaultRootState } from '@databases/redux';
import {
  type DatabaseField,
  DatabaseFieldType,
  type EntryWithContext,
  type Hyperlink,
  type RawDatabaseValue,
} from '@databases/types/database';
import { DatabaseFilterType } from '@databases/types/database/view';
import { isEqual } from 'lodash-es';

import { RECURSION_LIMIT } from '../../constants';
import { cheerioIterator } from '../../lib/dom';
import { selectBaseUrl } from '../../redux/context/selectors';
import { loadDatabaseByContentId } from '../../redux/databases/actions';
import {
  selectDatabaseByContentIdOptional,
  selectDatabaseByIdOptional,
} from '../../redux/databases/selectors';
import { getEntryPath, matchEntryPath } from '../../urls';
import { encodeHtml, generateId } from '../../utils';
import { type FieldType, loadDependencies } from '../field-types';
import {
  getDatabaseEntryOptional,
  getDatabaseValue,
  getEntryName,
  getFirstField,
  isBrokenDatabase,
  isNonBrokenDatabase,
} from '../utils';

/**
 * Returns the referenced entries from the target database.
 */
export function getEntryLinkTargetEntries<State extends DefaultRootState>(
  {
    field,
    value,
  }: { field: DatabaseField; value: RawDatabaseValue<DatabaseFieldType.ENTRY_LINK> },
  state: State,
  {
    selectDatabase = selectDatabaseByContentIdOptional,
  }: {
    selectDatabase?: typeof selectDatabaseByContentIdOptional;
  } = {}
): EntryWithContext[] {
  let _db = undefined;

  if (field.settings.databaseId && field.settings.databaseSpace) {
    _db =
      value &&
      selectDatabaseByIdOptional(state, {
        spaceKey: field.settings.databaseSpace?.key,
        id: field.settings.databaseId,
      });
  } else if (field.settings.contentId) {
    _db =
      value &&
      selectDatabaseByContentIdOptional(state, { contentId: field.settings.contentId });
  }
  const targetDatabase = _db && selectDatabase(state, { contentId: _db.contentId });
  if (!targetDatabase || isBrokenDatabase(targetDatabase)) {
    return [];
  }

  return value.filter(isNonEmptyEntryLinkValue).flatMap((entryRef) => {
    const targetEntry = getDatabaseEntryOptional(targetDatabase, entryRef.entryId);
    return targetEntry ? [{ database: targetDatabase, entry: targetEntry }] : [];
  });
}

function getCorrectedValue(
  {
    field,
    value,
  }: { field: DatabaseField; value: RawDatabaseValue<DatabaseFieldType.ENTRY_LINK> },
  state: DefaultRootState | undefined,
  keepUnknownEntries: boolean,
  recursionTracker = { recursionCount: 0 }
): RawDatabaseValue<DatabaseFieldType.ENTRY_LINK> {
  const targetDatabase =
    field.settings.databaseSpace?.key && field.settings.databaseId
      ? value &&
        state &&
        selectDatabaseByIdOptional(state, {
          spaceKey: field.settings.databaseSpace?.key,
          id: field.settings.databaseId,
        })
      : field.settings.contentId
      ? value &&
        state &&
        selectDatabaseByContentIdOptional(state, { contentId: field.settings.contentId })
      : undefined;
  if (
    targetDatabase &&
    isNonBrokenDatabase(targetDatabase) &&
    state &&
    recursionTracker.recursionCount < RECURSION_LIMIT
  ) {
    return value.flatMap((entryRef) => {
      if (!isNonEmptyEntryLinkValue(entryRef)) {
        return [entryRef];
      } else {
        const referencedEntry = getDatabaseEntryOptional(
          targetDatabase,
          entryRef.entryId
        );
        if (referencedEntry) {
          return [
            {
              ...entryRef,
              title: getEntryName(
                { database: targetDatabase, entry: referencedEntry },
                state,
                { recursionCount: recursionTracker.recursionCount + 1 }
              ),
            },
          ];
        } else {
          // Keep not found entry reference. It might link to a drafted entry that has not been saved yet.
          return keepUnknownEntries ? [entryRef] : [];
        }
      }
    });
  }
  return value;
}

function getStringRepresentation(
  context: {
    field: DatabaseField;
    value: RawDatabaseValue<DatabaseFieldType.ENTRY_LINK>;
  },
  state: DefaultRootState | undefined,
  recursionTracker = { recursionCount: 0 }
): string {
  const value = getCorrectedValue(context, state, false, recursionTracker);
  return (
    value
      ?.filter(isNonEmptyEntryLinkValue)
      .map((e) => (e.title === '' ? `Unnamed entry:${e.entryId}` : e.title))
      .join('\n') ?? ''
  );
}

function getHyperlinkRepresentation(
  context: {
    field: DatabaseField;
    value: RawDatabaseValue<DatabaseFieldType.ENTRY_LINK>;
  },
  state: DefaultRootState | undefined,
  recursionTracker = { recursionCount: 0 }
): Array<Hyperlink & { _id: string }> {
  const value = getCorrectedValue(context, state, false, recursionTracker);
  if (value && state) {
    const referencedDatabase =
      context.field.settings.databaseSpace?.key && context.field.settings.databaseId
        ? value &&
          selectDatabaseByIdOptional(state, {
            spaceKey: context.field.settings.databaseSpace?.key,
            id: context.field.settings.databaseId,
          })
        : context.field.settings.contentId
        ? value &&
          selectDatabaseByContentIdOptional(state, {
            contentId: context.field.settings.contentId,
          })
        : undefined;

    if (value && referencedDatabase) {
      const baseUrl = selectBaseUrl(state);
      return value
        .filter(isNonEmptyEntryLinkValue)
        .map((entryRef) => {
          return {
            url: `${baseUrl}${getEntryPath({
              contentId: referencedDatabase.contentId,
              entryId: entryRef.entryId,
              spaceKey: referencedDatabase.space.key,
            })}`,
            label: entryRef.title,
            _id: generateId(),
          };
        })
        .filter((entry) => entry.url);
    }
  }
  return [];
}

/**
 * Returns true if the given entry value is not empty. Empty entry values are added by the user through the UI
 * but have not been filled with an entry reference yet.
 */
export function isNonEmptyEntryLinkValue(
  value: NonNullable<RawDatabaseValue<DatabaseFieldType.ENTRY_LINK>>[0]
): value is NonNullable<RawDatabaseValue<DatabaseFieldType.ENTRY_LINK>>[0] & {
  entryId: string;
  title: string;
} {
  if (value && typeof value === 'object') {
    return 'entryId' in value;
  } else {
    return false;
  }
}

const entryLinkFieldType: FieldType<DatabaseFieldType.ENTRY_LINK> = {
  supportedFilterTypes: [
    DatabaseFilterType.EQUALS,
    DatabaseFilterType.CONTAINS,
    DatabaseFilterType.EMPTY,
  ],

  isCsvImportSupported: false,

  renderExportView: (context, state, renderContext, recursionCount) => {
    const value = getCorrectedValue(context, state, false, { recursionCount });
    const referencedDatabase =
      context.field.settings.databaseSpace?.key && context.field.settings.databaseId
        ? value &&
          selectDatabaseByIdOptional(state, {
            spaceKey: context.field.settings.databaseSpace?.key,
            id: context.field.settings.databaseId,
          })
        : context.field.settings.contentId
        ? value &&
          selectDatabaseByContentIdOptional(state, {
            contentId: context.field.settings.contentId,
          })
        : undefined;
    if (value && referencedDatabase) {
      const baseUrl = selectBaseUrl(state);
      return value
        .filter(isNonEmptyEntryLinkValue)
        .map(
          (entryRef) =>
            `<a href="${encodeHtml(
              `${baseUrl}${getEntryPath({
                contentId: referencedDatabase.contentId,
                entryId: entryRef.entryId,
                spaceKey: referencedDatabase.space.key,
              })}`
            )}">${encodeHtml(entryRef.title)}</a>`
        )
        .join('<br/>');
    } else {
      return '';
    }
  },

  parseFromHtml: (el, context, state) => {
    const baseUrl = selectBaseUrl(state);
    const targetDatabase =
      context.field.settings.databaseSpace?.key && context.field.settings.databaseId
        ? selectDatabaseByIdOptional(state, {
            spaceKey: context.field.settings.databaseSpace?.key,
            id: context.field.settings.databaseId,
          })
        : context.field.settings.contentId
        ? selectDatabaseByContentIdOptional(state, {
            contentId: context.field.settings.contentId,
          })
        : undefined;
    if (targetDatabase && isNonBrokenDatabase(targetDatabase)) {
      return [...cheerioIterator(el.find('a[href]'))].flatMap((link) => {
        const href = link.attr('href') ?? '';
        if (href.startsWith(baseUrl)) {
          const match = matchEntryPath(href);
          if (match && match.contentId === targetDatabase.contentId) {
            const entry = getDatabaseEntryOptional(targetDatabase, match.entryId);
            if (entry) {
              return [
                {
                  _id: generateId(),
                  title: getEntryName({ database: targetDatabase, entry }, state),
                  entryId: entry.id,
                },
              ];
            }
          }
        }
        return [];
      });
    } else {
      return [];
    }
  },

  getInitialValue: () => [],

  isEmptyValue: ({ value }) =>
    !value || value.filter(isNonEmptyEntryLinkValue).length === 0,

  isEqualValue: ({ value: value1 }, { value: value2 }) => {
    if (!value1 || !value2) {
      return !value1 === !value2;
    } else {
      if (Array.isArray(value1) && Array.isArray(value2)) {
        return isEqual(
          value1.filter(isNonEmptyEntryLinkValue).map((e) => e.entryId),
          value2.filter(isNonEmptyEntryLinkValue).map((e) => e.entryId)
        );
      } else {
        logError(
          new Error(
            `Entry link field values of types ${typeof value1} and ${typeof value2} are not both arrays`
          )
        );
        return false;
      }
    }
  },

  getStringRepresentation,

  loadDependencies: (
    { field, value },
    { dispatch, selector, selectDatabase, helpers },
    state,
    { recursionCount }
  ) => {
    if (recursionCount >= RECURSION_LIMIT) {
      return;
    }

    if (value) {
      if (field.settings.contentId) {
        dispatch(loadDatabaseByContentId(field.settings.contentId));
      }

      // Load main field dependencies of target entries
      for (const entryRef of value.filter(isNonEmptyEntryLinkValue)) {
        const [targetDatabaseId, targetFirstField, targetValue] = selector((state) => {
          const _db1 =
            field.settings.databaseSpace?.key && field.settings.databaseId
              ? selectDatabaseByIdOptional(state, {
                  spaceKey: field.settings.databaseSpace?.key,
                  id: field.settings.databaseId,
                })
              : field.settings.contentId
              ? selectDatabaseByContentIdOptional(state, {
                  contentId: field.settings.contentId,
                })
              : undefined;
          const _db2 = _db1 && selectDatabase(state, { contentId: _db1.contentId });
          const targetDatabase = _db2 && !isBrokenDatabase(_db2) ? _db2 : undefined;
          const targetFirstField = targetDatabase && getFirstField(targetDatabase);
          const targetEntry =
            targetDatabase && getDatabaseEntryOptional(targetDatabase, entryRef.entryId);
          const targetValue =
            targetFirstField &&
            targetEntry &&
            getDatabaseValue(targetDatabase, targetFirstField, targetEntry);
          return [targetDatabase?.id, targetFirstField, targetValue] as const;
        });

        if (targetDatabaseId && targetFirstField && targetValue) {
          loadDependencies(
            {
              contentId: targetDatabaseId,
              field: targetFirstField,
              entryId: entryRef.entryId,
              value: targetValue,
            },
            helpers,
            state,
            { recursionCount: recursionCount + 1 }
          );
        }
      }
    }
  },

  convertTo: {
    [DatabaseFieldType.TEXT]: (context, state, recursionCount) => ({
      plain: getStringRepresentation(context, state, { recursionCount }) ?? '',
    }),
    [DatabaseFieldType.HYPERLINK]: (context, state, recursionCount) =>
      getHyperlinkRepresentation(context, state, { recursionCount }),
  },
};

export default entryLinkFieldType;
